import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Select, message } from 'antd';
import get from 'lodash/get';
import { SegmentGroup } from '@fxtr/i18n';
import config from '$config';
import { segmentLabel } from '../CampaignSegmentSelect';
import { getSitewidePromo } from './api';
import { updateCampaign } from '../../containers/CampaignEditPage/actions';

const { Item } = Form;
const { Option } = Select;

export const SITEWIDE_PROMO_LABEL = 'Sitewide promo – show to ALL website customers';

const AUDIENCE_NO = 'no';
const AUDIENCE_PAID = 'paid';
const AUDIENCE_ORGANIC = 'organic';

/**
 * Maps the select onto the existing sitewide and inclusive flags.
 * No leaves sitewide off. Inclusive is unused then, and stays at the paid-traffic default.
 * Yes (organic & paid traffic) is sitewide on and inclusive. Organic traffic only is sitewide on and not inclusive.
 */
const audienceFor = (sitewide, inclusive) => {
  if (!sitewide) return AUDIENCE_NO;
  return inclusive ? AUDIENCE_PAID : AUDIENCE_ORGANIC;
};

const SitewidePromoContext = React.createContext(null);

export const SitewidePromoSelect = () => {
  const audienceSelect = useContext(SitewidePromoContext);
  if (!audienceSelect) return null;
  return audienceSelect;
};

const SITEWIDE_ACKNOWLEDGE_COPY =
  'Are you sure you want this promo to be shown to all users who come to the site (excluding partners, fleets, affiliates, premium membership)';

const idlePromo = {
  sitewide: false,
  inclusive: true,
  expectedRev: 0,
  acknowledgedSegment: '',
};

export const isFixterDeployment = (segmentGroup = config.segmentGroup) => segmentGroup === SegmentGroup.FIXTER;

const errorMessage = (error, fallback) => get(error, 'response.data.message') || fallback;

const SitewidePromoFields = ({ mode, segment, campaignId, onState, children, slotRefresh = 0 }) => {
  const [promo, setPromo] = useState(idlePromo);
  const [ready, setReady] = useState(mode !== 'edit');
  const [visible, setVisible] = useState(false);
  const [loadingSlot, setLoadingSlot] = useState(false);
  const [holder, setHolder] = useState(null);
  const [pendingRev, setPendingRev] = useState(0);
  const [slotMarket, setSlotMarket] = useState('');
  const onStateRef = useRef(onState);
  const pendingInclusiveRef = useRef(null);
  const segmentRef = useRef(segment);
  const slotRequestRef = useRef(0);
  const visibleRef = useRef(visible);

  onStateRef.current = onState;
  segmentRef.current = segment;
  visibleRef.current = visible;
  const { sitewide, inclusive, expectedRev, acknowledgedSegment } = promo;
  const slotMatchesMarket = slotMarket === (segment || '');
  const shownHolder = slotMatchesMarket ? holder : null;
  const shownRev = slotMatchesMarket ? pendingRev : 0;

  const loadSlot = useCallback(async (market) => {
    const requestId = ++slotRequestRef.current;
    setLoadingSlot(true);
    try {
      const slot = await getSitewidePromo(market);
      if (requestId !== slotRequestRef.current) return null;
      setSlotMarket(market);
      setPendingRev((slot && slot.rev) || 0);
      setHolder(slot && slot.id && slot.id !== campaignId ? slot : null);
      return slot;
    } catch (error) {
      if (requestId === slotRequestRef.current) throw error;
      return null;
    } finally {
      if (requestId === slotRequestRef.current) setLoadingSlot(false);
    }
  }, [campaignId]);

  const publishState = (next) => {
    if (onStateRef.current) onStateRef.current(next);
  };

  const commitPromo = (next) => {
    setPromo(next);
    publishState({
      sitewide: next.sitewide,
      inclusive: next.inclusive,
      expectedRev: next.expectedRev,
    });
  };

  const openModal = async (market) => {
    const resolved = market || segmentRef.current;
    setVisible(true);
    if (!resolved) return;

    try {
      await loadSlot(resolved);
    } catch (error) {
      pendingInclusiveRef.current = null;
      message.error(errorMessage(error, 'Could not check the current sitewide promo'), 3);
      setVisible(false);
    }
  };

  useEffect(() => {
    if (mode !== 'edit' || !segment) {
      setReady(true);
      return undefined;
    }

    let cancelled = false;
    getSitewidePromo(segment)
      .then((slot) => {
        if (cancelled) return;
        const rev = slot.rev || 0;
        if (slot.id === campaignId) {
          commitPromo({
            sitewide: true,
            inclusive: slot.inclusive !== false,
            expectedRev: rev,
            acknowledgedSegment: segment,
          });
        } else {
          setPromo((current) => ({ ...current, expectedRev: rev }));
        }
        setReady(true);
      })
      .catch((error) => {
        if (cancelled) return;
        message.error(errorMessage(error, 'Could not check the current sitewide promo'), 3);
        setReady(true);
      });

    return () => {
      cancelled = true;
    };
    // The slot is loaded once for this campaign. Market changes are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, mode]);

  useEffect(() => {
    if (mode === 'edit' || !segment) return undefined;
    // Share loadSlot's request id so a slower check cannot replace a newer market's slot.
    loadSlot(segment).catch((error) => {
      if (!visibleRef.current) return;
      message.error(errorMessage(error, 'Could not check the current sitewide promo'), 3);
      setVisible(false);
    });
    return undefined;
  }, [mode, segment, campaignId, loadSlot]);

  useEffect(() => {
    if (mode !== 'edit' || !slotRefresh || !segment) return undefined;

    let cancelled = false;
    getSitewidePromo(segment)
      .then((slot) => {
        if (cancelled) return;
        const rev = slot.rev || 0;
        if (slot.id !== campaignId) return;
        const next = {
          sitewide: true,
          inclusive: slot.inclusive !== false,
          expectedRev: rev,
          acknowledgedSegment: segment,
        };
        commitPromo(next);
      })
      .catch((error) => {
        if (cancelled) return;
        message.error(errorMessage(error, 'Could not check the current sitewide promo'), 3);
      });

    return () => {
      cancelled = true;
    };
    // A date or banner save bumps rev without sending sitewide. Refresh the slot so the next toggle is not a false 409.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotRefresh]);

  useEffect(() => {
    if (!ready || !sitewide) return;
    const market = segment || '';
    if (market === (acknowledgedSegment || '')) return;
    commitPromo({ ...idlePromo });
    openModal(segment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment, sitewide, acknowledgedSegment, ready]);

  const saveEdit = async (body) => {
    try {
      await updateCampaign(campaignId, body);
      const slot = body.sitewide ? await getSitewidePromo(segment) : { rev: 0 };
      const rev = slot.rev || 0;
      commitPromo({
        sitewide: Boolean(body.sitewide),
        inclusive: body.inclusive !== false,
        expectedRev: rev,
        acknowledgedSegment: body.sitewide ? segment || '' : '',
      });
      message.info(body.sitewide ? 'Sitewide promo saved' : 'Sitewide promo turned off', 2);
      return true;
    } catch (error) {
      const status = get(error, 'response.status');
      if (status === 409) {
        message.error('The sitewide promo for this market changed. Check it again.', 3);
        // Idle promo defaults inclusive to paid. Keep the failed choice so the retry does not flip organic to paid.
        if (body.sitewide) pendingInclusiveRef.current = body.inclusive === true;
        commitPromo({ ...idlePromo });
        openModal(segment);
        return false;
      }
      message.error(errorMessage(error, 'Could not update the sitewide promo'), 3);
      return false;
    }
  };

  const onAudienceChange = (nextAudience) => {
    switch (nextAudience) {
      case AUDIENCE_NO: {
        pendingInclusiveRef.current = null;
        setVisible(false);
        if (mode === 'edit') {
          saveEdit({ sitewide: false, expectedRev });
          return;
        }
        commitPromo({ ...idlePromo, expectedRev });
        return;
      }
      case AUDIENCE_PAID:
      case AUDIENCE_ORGANIC: {
        const nextInclusive = nextAudience === AUDIENCE_PAID;
        if (!sitewide) {
          pendingInclusiveRef.current = nextInclusive;
          openModal(segment);
          return;
        }
        if (mode === 'edit') {
          saveEdit({ sitewide: true, inclusive: nextInclusive, expectedRev });
          return;
        }
        commitPromo({ ...promo, inclusive: nextInclusive });
        return;
      }
      default:
        return;
    }
  };

  const acknowledge = async () => {
    const nextInclusive = pendingInclusiveRef.current === null ? inclusive : pendingInclusiveRef.current;
    if (mode === 'edit') {
      const saved = await saveEdit({ sitewide: true, inclusive: nextInclusive, expectedRev: shownRev });
      if (!saved) {
        pendingInclusiveRef.current = nextInclusive;
        return;
      }
      pendingInclusiveRef.current = null;
      setVisible(false);
      return;
    }
    pendingInclusiveRef.current = null;
    commitPromo({
      sitewide: true,
      inclusive: nextInclusive,
      expectedRev: shownRev,
      acknowledgedSegment: segment || '',
    });
    setVisible(false);
  };

  const dismiss = () => {
    pendingInclusiveRef.current = null;
    setVisible(false);
  };

  const holderText = shownHolder ? (
    <Alert
      type="warning"
      showIcon
      style={{ marginTop: 16 }}
      message={
        <span>
          {segmentLabel(shownHolder.segment || segment)} already uses{' '}
          <a href={`/campaigns/${shownHolder.id}`} target="_blank" rel="noopener noreferrer">
            {shownHolder.code || shownHolder.id}
          </a>{' '}
          as its sitewide promo. Acknowledging replaces it.
        </span>
      }
    />
  ) : null;

  const modal = (
    <Modal
      title="Sitewide promo"
      visible={visible}
      onCancel={dismiss}
      footer={
        <>
          <Button onClick={dismiss}>No</Button>
          <Button
            type="primary"
            disabled={loadingSlot || (mode === 'create' && (!segment || !slotMatchesMarket))}
            onClick={acknowledge}
          >
            Understood
          </Button>
        </>
      }
    >
      <p>{SITEWIDE_ACKNOWLEDGE_COPY}</p>
      {holderText}
      {mode === 'create' && !segment && <p>Choose a market to check the current sitewide promo.</p>}
    </Modal>
  );

  const audienceSelect = (
    <Select value={audienceFor(sitewide, inclusive)} onChange={onAudienceChange} style={{ width: 280 }}>
      <Option value={AUDIENCE_NO}>No</Option>
      <Option value={AUDIENCE_PAID}>Yes (organic & paid traffic)</Option>
      <Option value={AUDIENCE_ORGANIC}>Organic traffic only</Option>
    </Select>
  );

  if (mode === 'edit') {
    return (
      <SitewidePromoContext.Provider value={audienceSelect}>
        {children}
        {modal}
      </SitewidePromoContext.Provider>
    );
  }

  return (
    <>
      <Item label={SITEWIDE_PROMO_LABEL}>{audienceSelect}</Item>
      {modal}
    </>
  );
};

SitewidePromoFields.displayName = 'SitewidePromoFields';

export default SitewidePromoFields;
